import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { io } from 'socket.io-client';
import { API_ORIGIN, SKIP_API } from '../apiMode';

const CallContext = createContext(null);
const ICE = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };

export function CallProvider({ children }) {
  const [activeCall, setActiveCall] = useState(null);
  const [callStatus, setCallStatus] = useState('idle');
  const [callMessage, setCallMessage] = useState('');
  const [duration, setDuration] = useState(0);
  const [micOn, setMicOn] = useState(true);
  const [remoteAudioOn, setRemoteAudioOn] = useState(true);
  const socketRef = useRef(null), pcRef = useRef(null), localStreamRef = useRef(null), remoteAudioRef = useRef(null);
  const timerRef = useRef(null), generationRef = useRef(0), partnerRef = useRef(null);

  const release = useCallback(() => {
    clearInterval(timerRef.current); timerRef.current = null;
    const pc = pcRef.current; pcRef.current = null;
    if (pc) { pc.onconnectionstatechange = null; pc.close(); }
    socketRef.current?.disconnect(); socketRef.current = null;
    localStreamRef.current?.getTracks().forEach(t => t.stop()); localStreamRef.current = null;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
  }, []);
  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    setDuration(0); timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
  }, []);
  const endCall = useCallback(() => {
    generationRef.current += 1; partnerRef.current = null; release();
    setActiveCall(null); setCallStatus('idle'); setCallMessage(''); setDuration(0); setMicOn(true); setRemoteAudioOn(true);
  }, [release]);
  useEffect(() => () => { generationRef.current += 1; partnerRef.current = null; release(); }, [release]);

  const startCall = useCallback(async (partnerId, user) => {
    if (String(partnerRef.current) === String(partnerId)) return;
    generationRef.current += 1; release();
    partnerRef.current = partnerId;
    const generation = generationRef.current;
    const current = () => generation === generationRef.current;
    const roomId = `dm-${[String(user.id), String(partnerId)].sort().join('-')}`;
    setActiveCall({ partnerId }); setCallStatus('calling'); setCallMessage('マイクを準備しています');
    setDuration(0); setMicOn(true); setRemoteAudioOn(true);
    const fail = (message, status = 'failed') => {
      if (!current()) return;
      generationRef.current += 1; release(); partnerRef.current = null;
      setCallStatus(status); setCallMessage(message);
    };
    if (SKIP_API) { setCallMessage('プレビューでは通話できません'); return; }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false }); }
    catch { fail('マイクを利用できません。端末のマイク設定を確認してください。'); return; }
    if (!current()) { stream.getTracks().forEach(t => t.stop()); return; }
    localStreamRef.current = stream;
    setCallMessage('接続しています');
    const socket = io(API_ORIGIN, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;
    const createPeer = remoteSocketId => {
      const pc = new RTCPeerConnection(ICE); pcRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      pc.onicecandidate = e => { if (current() && e.candidate) socket.emit('talk:ice', { to: remoteSocketId, candidate: e.candidate, roomId }); };
      pc.ontrack = e => { if (current() && remoteAudioRef.current) remoteAudioRef.current.srcObject = e.streams[0]; };
      pc.onconnectionstatechange = () => {
        if (!current()) return;
        if (pc.connectionState === 'connected') { setCallStatus('connected'); setCallMessage('通話中'); startTimer(); }
        else if (pc.connectionState === 'disconnected') { setCallMessage('接続を確認しています'); }
        else if (pc.connectionState === 'failed') fail('接続が切れました。もう一度かけ直してください。');
      };
      return pc;
    };
    socket.on('connect', () => { if (current()) socket.emit('talk:join', { roomId, userId: user.id, username: user.username }); });
    socket.on('talk:joinDenied', ({ reason }) => fail(reason || 'この通話に参加できませんでした。'));
    socket.on('connect_error', () => fail('接続できませんでした。通信環境を確認してください。'));
    socket.on('talk:joined', async ({ peers }) => {
      if (!current()) return;
      try {
        const others = peers.filter(p => p.socketId !== socket.id);
        if (!others.length) { setCallMessage('相手の参加を待っています'); return; }
        const pc = createPeer(others[0].socketId);
        const offer = await pc.createOffer(); if (!current()) return;
        await pc.setLocalDescription(offer); if (!current()) return;
        socket.emit('talk:offer', { to: others[0].socketId, sdp: pc.localDescription, roomId });
      } catch { fail('通話を開始できませんでした。'); }
    });
    socket.on('talk:offer', async ({ from, sdp }) => {
      if (!current()) return;
      try {
        const pc = createPeer(from);
        await pc.setRemoteDescription(new RTCSessionDescription(sdp)); if (!current()) return;
        const answer = await pc.createAnswer(); if (!current()) return;
        await pc.setLocalDescription(answer); if (!current()) return;
        socket.emit('talk:answer', { to: from, sdp: pc.localDescription, roomId });
      } catch { fail('通話を開始できませんでした。'); }
    });
    socket.on('talk:answer', async ({ sdp }) => {
      if (!current() || !pcRef.current) return;
      try { await pcRef.current.setRemoteDescription(new RTCSessionDescription(sdp)); } catch { fail('通話を開始できませんでした。'); }
    });
    socket.on('talk:ice', async ({ candidate }) => {
      if (!current() || !pcRef.current) return;
      try { await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate)); } catch { /* Late candidates can arrive after reconnecting. */ }
    });
    socket.on('talk:peerLeft', () => fail('相手が通話を終了しました', 'ended'));
    socket.on('disconnect', () => { if (current()) setCallMessage('接続を確認しています'); });
  }, [release, startTimer]);

  const toggleMic = useCallback(() => {
    if (!localStreamRef.current) return;
    setMicOn(on => { localStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = !on; }); return !on; });
  }, []);
  const toggleRemoteAudio = useCallback(() => setRemoteAudioOn(on => !on), []);
  return <CallContext.Provider value={{ activeCall, setActiveCall, callStatus, setCallStatus, callMessage, duration, setDuration, micOn, toggleMic, remoteAudioOn, toggleRemoteAudio, socketRef, pcRef, localStreamRef, remoteAudioRef, startTimer, startCall, endCall }}>
    {children}<audio ref={remoteAudioRef} muted={!remoteAudioOn} autoPlay playsInline style={{ display: 'none' }} />
  </CallContext.Provider>;
}
export function useCall() { return useContext(CallContext); }
